import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

/**
 * Life pillar — relationships, health, sanity, heat, lifestyle.
 * All stats hide by default per DESIGN Tenet 7; manifest narratively
 * via Phone/Email storylines. Opt-in Stats Dashboard exposes raw numbers.
 */

export type RelationshipId = 'wife' | 'mom' | 'broker' | 'lawyer' | 'best_friend';

interface LifeStore {
  // Relationships
  relationships: Record<RelationshipId, number>;  // -100..100
  // Health & sanity
  health: number;        // 0..100
  sanity: number;        // 0..100
  // Heat (only rises by default)
  irsHeat: number;       // 0..100
  secHeat: number;
  fbiHeat: number;
  // Lifestyle
  apartmentTier: 'studio' | '1br' | 'loft' | 'penthouse' | 'mansion' | 'compound';
  insuranceStatus: 'normal' | 'high-risk' | 'uninsurable';
  creditScore: number;   // 300..850
  // Active retainers — drain cash per ms while engaged
  lawyerRetainerActiveSince: number | null;   // null = not active
  // Last decay tick
  lastDecayMs: number;

  bumpRelationship: (id: RelationshipId, delta: number) => void;
  bumpHealth: (delta: number) => void;
  bumpSanity: (delta: number) => void;
  bumpIrsHeat: (delta: number) => void;
  bumpSecHeat: (delta: number) => void;
  bumpFbiHeat: (delta: number) => void;
  setApartmentTier: (t: LifeStore['apartmentTier']) => void;
  setInsuranceStatus: (s: LifeStore['insuranceStatus']) => void;
  bumpCreditScore: (delta: number) => void;
  applyDecayUpTo: (now: number) => void;
  startLawyerRetainer: () => void;
  stopLawyerRetainer: () => void;
}

const INITIAL_RELATIONSHIPS: Record<RelationshipId, number> = {
  wife: 30,
  mom: 60,
  broker: 0,
  lawyer: 0,
  best_friend: 50,
};

export const useLife = create<LifeStore>()(
  immer((set) => ({
    relationships: INITIAL_RELATIONSHIPS,
    health: 80,
    sanity: 60,
    irsHeat: 0,
    secHeat: 0,
    fbiHeat: 0,
    apartmentTier: 'studio',
    insuranceStatus: 'normal',
    creditScore: 680,
    lawyerRetainerActiveSince: null,
    lastDecayMs: Date.now(),

    bumpRelationship: (id, delta) => set((s) => {
      s.relationships[id] = clamp(s.relationships[id] + delta, -100, 100);
    }),
    bumpHealth: (delta) => set((s) => {
      s.health = clamp(s.health + delta, 0, 100);
    }),
    bumpSanity: (delta) => set((s) => {
      s.sanity = clamp(s.sanity + delta, 0, 100);
    }),
    bumpIrsHeat: (delta) => set((s) => { s.irsHeat = clamp(s.irsHeat + delta, 0, 100); }),
    bumpSecHeat: (delta) => set((s) => { s.secHeat = clamp(s.secHeat + delta, 0, 100); }),
    bumpFbiHeat: (delta) => set((s) => { s.fbiHeat = clamp(s.fbiHeat + delta, 0, 100); }),
    setApartmentTier: (t) => set((s) => { s.apartmentTier = t; }),
    setInsuranceStatus: (status) => set((s) => { s.insuranceStatus = status; }),
    bumpCreditScore: (delta) => set((s) => {
      s.creditScore = clamp(s.creditScore + delta, 300, 850);
    }),
    startLawyerRetainer: () => set((s) => {
      if (s.lawyerRetainerActiveSince === null) s.lawyerRetainerActiveSince = Date.now();
    }),
    stopLawyerRetainer: () => set((s) => { s.lawyerRetainerActiveSince = null; }),
    applyDecayUpTo: (now) => set((s) => {
      const elapsedDays = (now - s.lastDecayMs) / 86_400_000;
      if (elapsedDays <= 0) return;
      // Decay rates per LIFE.md (per-day)
      s.relationships.wife = clamp(s.relationships.wife - 1.0 * elapsedDays, -100, 100);
      s.relationships.mom = clamp(s.relationships.mom - 0.3 * elapsedDays, -100, 100);
      s.relationships.best_friend = clamp(s.relationships.best_friend - 0.3 * elapsedDays, -100, 100);
      s.sanity = clamp(s.sanity - 2.0 * elapsedDays, 0, 100);
      s.health = clamp(s.health - 0.5 * elapsedDays, 0, 100);
      s.lastDecayMs = now;
    }),
  })),
);

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
